import * as ecModels from '../../../models'

import * as models from './models'
import type { Plugin } from './plugin'

export class Selectors<TTypeParams extends ecModels.TypeParams.Constraint> {
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getAllSelectedIds
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getAllSelected
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getFirstSelectedId
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getFirstSelected
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly isSelected
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly isEditing

  constructor(plugin: Plugin<TTypeParams>) {
    this.getAllSelectedIds = plugin.feature.createSelectorMethod(
      (params: models.SourceParams = {}) => {
        const { from, target } = models.SourceParams.withDefaults(params)
        return from === 'applied'
          ? plugin.applied.selectors.getFromSelectionLookup(target).asSelector
          : plugin.edited.selectors.getFromSelectionLookup(target).asSelector
      },
    )
    this.getAllSelected = plugin.feature.createSelectorMethod(
      (params: models.SourceParams = {}) =>
        this.getAllSelectedIds(params).project(
          plugin.collection.plugins.cachePlugin.feature.selectors.lookup
            .asSelector,
          // TODO: Figure out what to do with undefined/missing entities. We
          // could filter them out or we could leave them undefined, or we
          // could have some way of creating a default entity, with just the
          // id
          (ids, lookup) => ids.map(id => lookup[id]),
        ).asSelector,
    )

    this.getFirstSelectedId = plugin.feature.createSelectorMethod(
      (params: models.SourceParams = {}) =>
        // NOTE: Explicit return type is necessary because TypeScript assumes
        // that indexing an array always returns an item. However, indexing an
        // empty array returns undefined.
        this.getAllSelectedIds(params).project(
          (ids): ecModels.EntityId | undefined => ids[0],
        ).asSelector,
    )
    this.getFirstSelected = plugin.feature.createSelectorMethod(
      (params: models.SourceParams = {}) =>
        this.getFirstSelectedId(params).project(
          plugin.collection.plugins.cachePlugin.feature.selectors.lookup
            .asSelector,
          (id, lookup) => (id ? lookup[id] : undefined),
        ).asSelector,
    )

    this.isSelected = plugin.feature.createSelectorMethod(
      (
        params: models.IdentityParams<TTypeParams> & models.SourceParams,
        behavior: 'every' | 'some' = 'every',
      ) => {
        const ids = models.IdentityParams.from(params, plugin.collection)
        return this.getAllSelectedIds(params).project(selectedIds =>
          ids[behavior](id => selectedIds.includes(id)),
        ).asSelector
      },
    )

    const editingTracker = plugin.feature.state.project(
      state => state.editedTargets,
    )
    this.isEditing = plugin.feature.createSelectorMethod(
      ({
        target = ecModels.Target.DEFAULT,
      }: {
        readonly target?: ecModels.Target
      } = {}) =>
        editingTracker.project(_editingTracker => _editingTracker.has(target))
          .asSelector,
    )
  }
}
